import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DecisionTable from '../components/DecisionTable';
import BanModal from '../components/BanModal';
import UnbanModal from '../components/UnbanModal';
import Stats from '../components/Stats';

const s = {
  layout: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--bg)',
  },
  header: {
    background: 'var(--bg2)',
    borderBottom: '1px solid var(--border)',
    padding: '0 2rem',
    height: '56px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  logo: {
    fontWeight: 800,
    fontSize: '1.1rem',
    letterSpacing: '-0.01em',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  userbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    fontFamily: 'var(--font-mono)',
    fontSize: '0.75rem',
    color: 'var(--muted)',
  },
  main: {
    flex: 1,
    padding: '2rem',
    maxWidth: '1400px',
    margin: '0 auto',
    width: '100%',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
  },
  searchWrap: {
    flex: 1,
    minWidth: '200px',
    position: 'relative',
  },
  searchInput: {
    width: '100%',
    background: 'var(--bg2)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
    padding: '0.6rem 0.6rem 0.6rem 2.4rem',
    color: 'var(--text)',
    fontFamily: 'var(--font-mono)',
    fontSize: '0.85rem',
    outline: 'none',
  },
  btnPrimary: {
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius)',
    padding: '0.6rem 1.2rem',
    fontFamily: 'var(--font-ui)',
    fontWeight: 700,
    fontSize: '0.85rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    whiteSpace: 'nowrap',
  },
  btnSecondary: {
    background: 'transparent',
    color: 'var(--muted)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
    padding: '0.6rem 1rem',
    fontFamily: 'var(--font-ui)',
    fontWeight: 600,
    fontSize: '0.85rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
};

export default function Dashboard({ user, onLogout }) {
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [localOnly, setLocalOnly] = useState(true);
  const [banModal, setBanModal] = useState(false);
  const [unbanTarget, setUnbanTarget] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/decisions');
      setDecisions(r.data || []);
      setLastRefresh(new Date());
    } catch {
      setDecisions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Filtre local — instantané, pas d'appel API
  const filtered = decisions
    .filter(d => !localOnly || d.origin === 'crowdsec' || d.origin === 'cscli')
    .filter(d => !searchInput.trim() || (d.value || '').includes(searchInput.trim()));

  const handleLogout = async () => {
    await axios.post('/auth/logout');
    onLogout();
  };

  const handleUnban = (decision) => setUnbanTarget(decision);

  const handleUnbanDone = () => {
    setUnbanTarget(null);
    load();
  };

  const handleBanDone = () => {
    setBanModal(false);
    load();
  };

  return (
    <div style={s.layout}>
      {/* Header */}
      <header style={s.header}>
        <div style={s.logo}>
          <span>🛡️</span> CrowdSec Manager
        </div>
        <div style={s.userbar}>
          <span>{user.email}</span>
          <button onClick={handleLogout} style={s.btnSecondary}>Déconnexion</button>
        </div>
      </header>

      <main style={s.main}>
        {/* Stats */}
        <Stats decisions={decisions} />

        {/* Toolbar */}
        <div style={s.toolbar}>
          <div style={s.searchWrap}>
            <span style={{ position:'absolute', left:'0.7rem', top:'50%', transform:'translateY(-50%)', color:'var(--muted)', fontSize:'1rem' }}>🔍</span>
            <input
              style={s.searchInput}
              placeholder="Filtrer par IP…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
          </div>

          <button
            style={{
              ...s.btnSecondary,
              color: localOnly ? 'var(--text)' : 'var(--muted)',
              borderColor: localOnly ? 'var(--accent)' : 'var(--border)',
            }}
            onClick={() => setLocalOnly(!localOnly)}
          >
            {localOnly ? '🌐 Tout afficher' : '🏠 Bans locaux seulement'}
          </button>

          <button style={s.btnSecondary} onClick={load}>
            ↻ Actualiser
          </button>
          <button style={s.btnPrimary} onClick={() => setBanModal(true)}>
            + Bannir une IP
          </button>
        </div>

        {lastRefresh && (
          <p style={{ fontFamily:'var(--font-mono)', fontSize:'0.7rem', color:'var(--muted)', marginBottom:'1rem' }}>
            Dernière mise à jour : {lastRefresh.toLocaleTimeString('fr-FR')}
            {' · '}{filtered.length} / {decisions.length} décision{decisions.length > 1 ? 's' : ''}
          </p>
        )}

        {/* Table */}
        <DecisionTable
          decisions={filtered}
          loading={loading}
          onUnban={handleUnban}
        />
      </main>

      {/* Modals */}
      {banModal && <BanModal onClose={() => setBanModal(false)} onDone={handleBanDone} />}
      {unbanTarget && <UnbanModal decision={unbanTarget} onClose={() => setUnbanTarget(null)} onDone={handleUnbanDone} />}
    </div>
  );
}
